import lodashGet from "lodash/get";
import lodashSet from "lodash/set";
import lodashHas from "lodash/has";

const VALUES_LIKE_BOOLEAN = ["true", "false"];

export const NOOP = () => {};

/**
 * Check if the input value is a string.
 */
export function isString(input: any): boolean {
  return typeof input === "string";
}

/**
 * Check if the input value is a number.
 */
export function isNumber(input: any): boolean {
  return typeof input === "number";
}

/**
 * Check if the input value is a boolean.
 */
export function isBoolean(input: any): boolean {
  return typeof input === "boolean";
}

/**
 * Check if the input resembles a boolean in a different form.
 */
export function likeBoolean(input: any): boolean {
  return isBoolean(input) || VALUES_LIKE_BOOLEAN.includes(input);
}

/**
 * Convert an input to a boolean, taking into account any lookie-likeys.
 */
export function toBoolean(input: any): boolean {
  switch (input) {
    case "true":
    case "1":
      return true;

    case "false":
    case "0":
    case "null":
    case "undefined":
      return false;

    default:
      return !!input;
  }
}

/**
 * Check if the input value is an array.
 */
export function isArray<T = any>(input: T): boolean {
  return input instanceof Array;
}

/**
 * Check if the input value is an object.
 */
export function isObject(input: any): boolean {
  return Object.prototype.toString.call(input) === "[object Object]";
}

/**
 * Check if the input value is RegExp.
 */
export function isRegExp(input: any): boolean {
  return input instanceof RegExp;
}

/**
 * Check if input value is a function.
 */
export function isFunction(input: any): boolean {
  return typeof input === "function";
}

/**
 * Check if an input value has been defined (is not undefined nor null).
 *
 * Note that empty strings are classed as defined.
 */
export function isDefined(input: any): boolean {
  return input !== undefined && input !== null;
}

/**
 * Check if an input value is empty.
 */
export function isEmpty(input: any): boolean {
  return (
    !isDefined(input) ||
    input === "" ||
    (isArray(input) && input.length === 0) ||
    (isObject(input) && Object.keys(input).length === 0)
  );
}

/**
 * Check if an input value is not empty.
 */
export function isNotEmpty(input: any): boolean {
  return !isEmpty(input);
}

/**
 * Check if input value is truthy.
 */
export function isTruthy(input: any): boolean {
  return !!input;
}

/**
 * Check if input value is falsy.
 */
export function isFalsy(input: any): boolean {
  return !input;
}

/**
 * Coerce a value to its most-likely-to-be type.
 */
export function coerceType(input: any): any {
  // Ignore non-strings
  if (!isString(input)) {
    return input;
  }

  // Boolean
  if (likeBoolean(input)) {
    return toBoolean(input);
  }

  // Number
  if (!isNaN(Number(input))) {
    return +input;
  }

  // Object/array
  try {
    return JSON.parse(input);
  } catch (error) {}

  return input;
}

export type ObjectPath = string | number | (string | number)[];
export type HasExpectedFn = (input: any) => boolean;
export type HasExpectedValue = HasExpectedFn | any;

/**
 * Funky alias for lodash/get.
 */
export function get(obj: any, prop: ObjectPath, defaultValue?: any): any {
  return lodashGet(obj, prop, defaultValue);
}

/**
 * Check if the input object has a named property.
 *
 * The `expectedValue` prop can also be a function which evaluates the
 * retrieved value on a true/false basis.
 */
export function has(
  obj: any,
  prop: ObjectPath,
  expectedValue?: HasExpectedValue
): boolean {
  const _value = get(obj, prop);
  const _has = !!obj && (lodashHas(obj, prop) || _value !== undefined);
  if (expectedValue === undefined) {
    return _has;
  } else {
    return isFunction(expectedValue)
      ? expectedValue(_value)
      : expectedValue === _value;
  }
}

/**
 * Check if the input object has a named property and if that property is not empty.
 */
export function hasAndIsNotEmpty(obj: any, prop: ObjectPath): boolean {
  return has(obj, prop, isNotEmpty);
}

type PipedFn = Function;
type PipedFnWithProps = [Function, ...any[]];
type PipedFunction = PipedFn | PipedFnWithProps;

/**
 * With an input, pipe a number of functions (potentially with extra params) to affect its
 * output.
 */
export function pipe(input: any, ...fns: PipedFunction[]) {
  return fns.reduce((acc: any, doFn: PipedFunction) => {
    if (isArray(doFn)) {
      let [fn, ...props] = doFn as PipedFnWithProps;
      acc = fn.call(acc, acc, ...props);
    } else {
      acc = (doFn as PipedFn).call(acc, acc);
    }
    return acc;
  }, input);
}

export function pipedFn(fn: PipedFn, ...initialProps: any[]) {
  return (input: any, ...extraProps: any[]) => {
    let curriedProps =
      initialProps && extraProps
        ? [...initialProps, ...extraProps.slice(initialProps.length)]
        : initialProps || extraProps;
    curriedProps.unshift(input);
    return fn.call(input, ...curriedProps);
  };
}

/**
 * Run a function against each item in an array, or just pass the value through if not an array.
 *
 * This is mainly for piping functions.
 */
export function map(input: any, fn: PipedFn, ...props: any[]) {
  if (isArray(input)) {
    return input.map(fn, ...props);
  } else {
    return input;
  }
}

/**
 * Inverse (or reverse) a boolean, string or array.
 */
export function inverse(input: boolean | string | any[]) {
  if (isBoolean(input)) {
    let output = !!input;
    return !output;
  } else if (isString(input)) {
    return (input as string)
      .split("")
      .reverse()
      .join("");
  } else if (isArray(input)) {
    return [...(input as any[])].reverse();
  }
  return input;
}

/**
 * Add leading zeros to a number.
 */
export function leadingZero(input: string | number, length = 2): string {
  let outputAsNumber: number = parseInt(input as string, 10);
  let output = isNaN(outputAsNumber) ? "0" : outputAsNumber + "";
  return `${
    output.length < length ? "0".repeat(length - output.length) : ""
  }${output}`;
}

/**
 * Convert an input to a string.
 */
export function toString(input: any) {
  return `${input}`;
}

/**
 * Convert an input to a number.
 */
export function toNumber(input: any) {
  if (isNumber(input)) {
    return input;
  } else if (isString(input)) {
    return parseFloat(input);
  } else {
    return 0;
  }
}

/**
 * Convert an input to an array.
 */
export function toArray(input: any, delimiter?: string | RegExp): any[] {
  if (isString(input) && (!!delimiter || delimiter === "")) {
    return String(input).split(delimiter);
  }
  return isArray(input) ? [...input] : isDefined(input) ? [input] : [];
}

/**
 * Check if any of the values are within the input array.
 */
export function anyInArray(input: any, ...values: any[]): boolean {
  if (!isArray(input)) {
    return false;
  }

  let output = false;
  let totalValues = values.length;
  if (totalValues) {
    for (let index = 0; index < totalValues; index++) {
      // Stop on first match
      if (input.indexOf(values[index]) > -1) {
        output = true;
        break;
      }
    }
  }

  return output;
}

/**
 * Check if all of the values are within the input array.
 */
export function allInArray(input: any, ...values: any[]): boolean {
  if (!isArray(input)) {
    return false;
  }

  let output = false;
  let totalValues = values.length;
  let countMatched = 0;
  if (totalValues) {
    for (let index = 0; index < totalValues; index++) {
      if (input.indexOf(values[index]) > -1) {
        countMatched++;

        // Must match all
        if (countMatched === totalValues) {
          output = true;
          break;
        }
      }
    }
  }

  return output;
}

/**
 * Calculate the number of total valid property value within an object.
 */
export function calcTotalValidProperties(input: { [propName: string]: any }) {
  return Object.values(input).reduce(
    (total, val) => (!!val ? total + 1 : total),
    0
  );
}

/**
 * Calculate the total length of an object whose properties are all
 * arrays.
 */
export function calcPropertiesTotalLength(input: {
  [propName: string]: any[];
}): number {
  return Object.values(input).reduce(
    (totalLength, arr) => totalLength + arr.length,
    0
  );
}

/**
 * Remove any properties whose value is null or undefined.
 */
export function stripNullProperties<T extends object = object>(
  input: T,
  options?: { returnNullIfEmpty?: boolean }
): T | null {
  const { returnNullIfEmpty } = options || { returnNullIfEmpty: true };

  const output: T = Object.keys(input).reduce((_output, propName) => {
    const propValue = get(input, propName);
    if (propValue !== null && propValue !== undefined) {
      lodashSet(_output, propName, propValue);
    }
    return _output;
  }, {}) as T;

  if (returnNullIfEmpty && Object.keys(output).length === 0) {
    return null;
  }

  return output;
}

type FindByPropValueFnCustomFunction = (
  value: any,
  item: any,
  propName: string
) => boolean;

/**
 * Generate a curried function to match an object's specific named property value.
 *
 * Used for finding and filtering arrays of objects.
 */
export function findByPropValueFn(
  propName: string,
  value: any
): (item: any) => boolean {
  return item => has(item, propName) && get(item, propName) === value;
}

/**
 * Generate a curried function to match an object's specific named property value.
 *
 * Used for finding and filtering arrays of objects.
 *
 * Also conveniently allows to match the value via a custom function {FindByPropValueFnCustomFunction}, or via RegExp.
 */
export function findByPropFn(
  propName: string,
  value: FindByPropValueFnCustomFunction | RegExp | any
): (item: any) => boolean {
  // Check via a custom function
  if (isFunction(value)) {
    return item =>
      has(item, propName) &&
      value.call(item, get(item, propName), item, propName);
  }
  // Check via RegExp
  else if (isRegExp(value)) {
    return item => has(item, propName) && value.test(get(item, propName));
  }
  // Check via value equivalence
  else {
    return findByPropValueFn(propName, value);
  }
}

type SortDirection = 1 | -1;

/**
 * Generate a curried function to sort an array of objects on a specific named property value.
 *
 * Used for sorting arrays of objects.
 */
export function sortAlphabeticallyByPropFn(
  propName: string,
  direction: SortDirection = 1
): (a: any, b: any) => number {
  return (a, b) => {
    const aValue = get(a, propName);
    const bValue = get(b, propName);
    return aValue === bValue ? 0 : aValue.localeCompare(bValue) * direction;
  };
}

/**
 * Generate a curried function to filter an array of objects by a specific named property value.
 */
export function filterByUniquePropFn(propName: string): (item: any) => boolean {
  let values: any[] = [];
  return item => {
    const value = get(item, propName);
    if (values.indexOf(value) === -1) {
      values.push(value);
      return true;
    } else {
      return false;
    }
  };
}

/**
 * Change "Crème Brulée" to "Creme Brulee" which helps with alphabetical
 * sorting and matching values.
 */
export function normaliseLatinDiacritics(input: string) {
  return `${input}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

const LENGTH_OF_DAY = 1000 * 60 * 60 * 24;

/**
 * Adjust a date by a certain amount of days.
 *
 * Returns the adjusted date as a YYYY-MM-DD string.
 */
export function adjustDate(date: string | Date, numOfDays: number): string {
  const _date = date instanceof Date ? date : new Date(date);
  const adjustedDate = new Date(_date.getTime() + numOfDays * LENGTH_OF_DAY);
  const outputDate = `${adjustedDate.getUTCFullYear()}-${leadingZero(
    adjustedDate.getUTCMonth() + 1,
    2
  )}-${adjustedDate.getUTCDate()}`;
  return outputDate;
}

/*
 * returns date as ISO-8601 string:
 * e.g. 2016-07-16T19:20:30+5:30
 */
export function getISO8601DateStr(date: Date = new Date()): string {
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  date.setMilliseconds(0);
  return date.toISOString().replace(".000Z", getTimezoneOffsetString());
}

/*
 * returns timezone string
 * e.g. +5:30
 */
export function getTimezoneOffsetString(date: Date = new Date()): string {
  const addZero = function(num: number) {
    if (num < 10) {
      return "0" + num;
    }
    return num;
  };
  let offset = date.getTimezoneOffset();
  const sign = offset < 0 ? "+" : "-";
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset / 60);
  const mins = Math.floor(absOffset % 60);
  return `${sign}${addZero(hours)}:${addZero(mins)}`;
}

/*
 * Copies Array childs of srcObj into the same property in destObj
 * (deepmerge merges the arrays, but we want to overwrite it instead)
 */
export function replaceArraysChilds(destObj: any, srcObj: any) {
  for (var prop in srcObj) {
    let val = srcObj[prop];
    if (Array.isArray(val)) {
      destObj[prop] = val;
    } else if (val instanceof Object) {
      replaceArraysChilds(destObj[prop], val);
    }
  }
}

/*
 * Returns current app address for auth services
 */
export function getRetUrl(fullUrl?: boolean) {
  let retUrl = fullUrl
    ? window.location.href
    : window.location.origin + window.location.pathname;
  if (retUrl.endsWith("?")) {
    retUrl = retUrl.substr(0, retUrl.length - 1);
  }
  if (retUrl.endsWith("/")) {
    retUrl = retUrl.substr(0, retUrl.length - 1);
  }
  return retUrl;
}
